import { Private_Routes, Public_Routes, VAPP_Private_Routes } from '@common/url';
import { useVerticalAppFlag } from '@common/VerticalApps/VerticalAppContext';
import {
  HelpCircleIcon,
  SettingsBIcon,
  LogOutAIcon,
  BuildingEIcon,
  CheckCircleIcon,
  UsersAIcon
} from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Ellipsis, Spinner } from '@prophecy/ui-v3';
import { Avatar } from '@prophecy/ui-v3/Avatar';
import { Button } from '@prophecy/ui-v3/Button';
import { Dropdown, DropdownProps } from '@prophecy/ui-v3/Dropdown';
import { Stack } from '@prophecy/ui-v3/Layout';
import { hideScrollbarCss } from '@prophecy/ui-v3/Misc/HideScrollbar';
import { ProgressBar } from '@prophecy/ui-v3/ProgressBar';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { Text } from '@prophecy/ui-v3/Typography';
import { useLocationPathName } from '@prophecy/utils/history';
import { roundToDecimals } from '@prophecy/utils/number';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useUser } from 'frontend/core/src/context/user';
import { completeLogoutAndReloadPage } from 'frontend/core/src/data/apis/api';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { USAGE_AND_BILLING_PAGE } from './credits/constants';
import { useProfessionalCreditStatus } from './credits/ProfessionalEdition/hooks';
import { useProfessionalEditionCreditsContext } from './credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { isUsageAndBillingPage } from './credits/ProfessionalEdition/utils';
import { useUserWorkspace } from './VerticalApps/hooks';

const CREDIT_USAGE_TOOLTIP_WIDTH = 320;
const USER_PROFILE_DROPDOWN_WIDTH = 400;
const WORKSPACE_TOOLTIP_WIDTH = 210;

type UserProfileDropdownProps = {
  children?: React.ReactNode;
  placement?: DropdownProps['placement'];
  align?: DropdownProps['align'];
  placementOffset?: DropdownProps['placementOffset'];
  onVisibleChange?: DropdownProps['onVisibleChange'];
  onInteractOutside?: DropdownProps['onInteractOutside'];
  collapseSideNav?: () => void;
};

export const UserProfileDropdown = ({
  children,
  placement,
  align,
  placementOffset,
  onVisibleChange,
  onInteractOutside,
  collapseSideNav
}: UserProfileDropdownProps) => {
  const user = useUser();
  const userName = `${user.user?.firstName} ${user.user?.lastName}`;
  const { serviceCreditsConsumed, serviceCreditsAllocated, serviceUsagePercentage } = useProfessionalCreditStatus();
  const { error, isLoading } = useProfessionalEditionCreditsContext();
  const navigate = useNavigate();
  const location = useLocation();

  const pathname = useLocationPathName();
  const isOnUsageAndBillingPage = isUsageAndBillingPage(pathname);
  const { workspaces, currentWorkspaceId, setCurrentWorkspaceId } = useUserWorkspace();

  const isVerticalApp = useVerticalAppFlag();

  const handleWorkspaceSwitch = usePersistentCallback((workspaceId: string) => {
    const isSwitchingWorkspace = workspaceId !== currentWorkspaceId;
    setCurrentWorkspaceId(workspaceId);
    // Projects are workspace-scoped, so leave an open project on switch and go to home.
    if (isSwitchingWorkspace && matchPath(VAPP_Private_Routes.Project_Detail.url, location.pathname)) {
      navigate(VAPP_Private_Routes.Home.getUrl());
    }
  });

  const handleSettingsClick = () => {
    collapseSideNav?.();
    navigate(Private_Routes.Settings.home.getUrl());
  };

  const handleHelpClick = () => {
    collapseSideNav?.();
    window.open(Public_Routes.Community.getUrl(), '_blank');
  };

  return (
    <Dropdown
      portalled={false}
      placement={placement}
      align={align}
      placementOffset={placementOffset}
      onVisibleChange={onVisibleChange}
      onInteractOutside={onInteractOutside}
      overlay={
        <Stack direction='vertical' gap={theme.spaces.x4}>
          <UserDetails direction='horizontal' gap={theme.spaces.x12} width='100%'>
            <Avatar size='m' text={userName} />
            <Stack direction='vertical' gap={theme.spaces.x2}>
              <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
                {userName}
              </Text>
              <Text level='sm' tone={theme.colors.gray600}>
                {user.user?.email}
              </Text>
            </Stack>
          </UserDetails>
          <Dropdown.Separator />
          {isVerticalApp && workspaces?.length > 0 && (
            <WorkspacesSection direction='vertical' gap={theme.spaces.x4}>
              <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.semiBold}>
                Workspaces
              </Text>
              <WorkspaceList direction='vertical' gap={theme.spaces.x2}>
                {workspaces?.map((workspace) => {
                  const isActive = workspace._id === currentWorkspaceId;
                  const isSharedWorkspace = workspace.createdBy !== user.user?._id;

                  return (
                    <WorkspaceItem key={workspace._id} onClick={() => handleWorkspaceSwitch(workspace._id)}>
                      <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
                        <WorkspaceIconWrapper $active={isActive}>
                          <BuildingEIconWrapper $active={isActive}>
                            <BuildingEIcon type='default' />
                          </BuildingEIconWrapper>
                          {isActive && <StyledCheckCircleIcon type='default' size='xs' />}
                        </WorkspaceIconWrapper>
                        <Ellipsis tooltip={true}>
                          <Text level='sm' tone={theme.colors.gray900} weight={theme.fontWeight.normal}>
                            {workspace.workspaceAlias || workspace.name}
                          </Text>
                        </Ellipsis>
                        {isSharedWorkspace && (
                          <Tooltip
                            overlayStyle={{
                              width: WORKSPACE_TOOLTIP_WIDTH,
                              padding: theme.spaces.x12
                            }}
                            title={
                              <Stack direction='vertical' gap={theme.spaces.x4}>
                                <Text level='xs' tone={theme.colors.white} weight={theme.fontWeight.semiBold}>
                                  Shared workspace
                                </Text>
                                <Text level='xs' tone={theme.colors.gray300} weight={theme.fontWeight.normal}>
                                  {workspace.createdByFullName} added you to their workspace
                                </Text>
                              </Stack>
                            }>
                            <UsersAIconWrapper>
                              <UsersAIcon type='default' size='xs' />
                            </UsersAIconWrapper>
                          </Tooltip>
                        )}
                      </Stack>
                    </WorkspaceItem>
                  );
                })}
              </WorkspaceList>
              <Dropdown.Separator />
            </WorkspacesSection>
          )}
          {isVerticalApp && (
            <>
              <CreditUsageContainer direction='vertical'>
                <CreditUsage direction='vertical' gap={theme.spaces.x8}>
                  <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center' width='100%'>
                    <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.semiBold}>
                      Credit Usage
                    </Text>
                    <Tooltip
                      title='The total credits consumed by your account. The credit usage is reset every month.'
                      placement='top'
                      overlayStyle={{ width: CREDIT_USAGE_TOOLTIP_WIDTH }}>
                      <span>
                        <StyledHelpCircleIcon type='default' color={theme.colors.gray500} size='xs' />
                      </span>
                    </Tooltip>
                  </Stack>
                  {isLoading ? (
                    <Spinner size='s' />
                  ) : error ? (
                    <Text level='xs' tone={theme.colors.error500}>
                      {error.message}
                    </Text>
                  ) : (
                    <>
                      <ProgressBar
                        value={serviceUsagePercentage}
                        color={theme.colors.brand600}
                        variant={'success'}
                        width='100%'
                      />
                      <Stack direction='horizontal' gap={theme.spaces.x4} align='space-between' alignY='center'>
                        {!isOnUsageAndBillingPage && (
                          <ViewMoreButton
                            variant='link'
                            size='xs'
                            onClick={() => {
                              navigate(USAGE_AND_BILLING_PAGE);
                            }}
                            data-test-id='view-more-button'>
                            <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.medium}>
                              View more
                            </Text>
                          </ViewMoreButton>
                        )}
                        <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.normal}>
                          {roundToDecimals(serviceCreditsConsumed)} / {roundToDecimals(serviceCreditsAllocated)} credits
                          consumed ({serviceUsagePercentage}%)
                        </Text>
                      </Stack>
                    </>
                  )}
                </CreditUsage>
              </CreditUsageContainer>

              <Dropdown.Separator />
            </>
          )}
          <Dropdown.Item data-test-id='settings-button' onClick={handleSettingsClick}>
            <StyledDropdownItem>
              <SettingsBIcon type='default' color={theme.colors.gray500} />

              <Text level='sm' tone={theme.colors.gray900}>
                Settings
              </Text>
            </StyledDropdownItem>
          </Dropdown.Item>
          <Dropdown.Item data-test-id='help-button' onClick={handleHelpClick}>
            <StyledDropdownItem>
              <HelpCircleIcon type='default' color={theme.colors.gray500} />

              <Text level='sm' tone={theme.colors.gray900}>
                Help
              </Text>
            </StyledDropdownItem>
          </Dropdown.Item>
          <Dropdown.Separator />
          <Dropdown.Item
            data-test-id='logout-button'
            onClick={() => {
              completeLogoutAndReloadPage();
            }}>
            <StyledDropdownItem>
              <LogOutAIcon type='default' color={theme.colors.gray500} />

              <Text level='sm' tone={theme.colors.gray900}>
                Sign out
              </Text>
            </StyledDropdownItem>
          </Dropdown.Item>
        </Stack>
      }
      data-test-id={'professional-edition-header-dropdown'}
      overlayStyle={{
        overflowY: 'auto',
        width: USER_PROFILE_DROPDOWN_WIDTH,
        marginRight: `${theme.spaces.x12}`
      }}>
      {children ?? (
        <Button variant='plain' size='s' data-test-id='professional-edition-header-dropdown-button'>
          <Avatar size='s' text={userName} />
        </Button>
      )}
    </Dropdown>
  );
};

const UserDetails = styled(Stack)`
  padding: ${theme.spaces.x12};
`;

const CreditUsageContainer = styled(Stack)`
  padding: ${theme.spaces.x12};
`;

const CreditUsage = styled(Stack)`
  padding: ${theme.spaces.x12} ${theme.spaces.x16};
  background: ${theme.colors.gray50};
  border-radius: ${theme.radius.x12};
`;

const StyledDropdownItem = styled(Dropdown.Item)`
  gap: ${theme.spaces.x8};
  direction: horizontal;
  align: center;
`;

const ViewMoreButton = styled(Button)`
  color: ${theme.colors.gray600};
  text-decoration: underline;'
  font-weight: ${theme.fontWeight.normal};
` as unknown as typeof Button;
const StyledHelpCircleIcon = styled(HelpCircleIcon)`
  cursor: pointer;
`;

const WorkspaceList = styled(Stack)`
  max-height: 160px;
  overflow-y: auto;
  ${hideScrollbarCss}
  padding: ${theme.spaces.x4} 0;
`;

const WorkspaceItem = styled(Stack)`
  padding: ${theme.spaces.x6} ${theme.spaces.x8};
  border-radius: ${theme.radius.x6};
  cursor: pointer;
`;

const WorkspacesSection = styled(Stack)`
  padding: ${theme.spaces.x4} ${theme.spaces.x12};
`;

const WorkspaceIconWrapper = styled(Stack)<{ $active: boolean }>`
  border: ${(p) => (p.$active ? `1px solid ${theme.colors.brand500}` : 'none')};
  border-radius: ${theme.radius.x6};
  padding: ${theme.spaces.x1};
  position: relative;
`;

const BuildingEIconWrapper = styled(Stack)<{ $active: boolean }>`
  color: ${(p) => (p.$active ? theme.colors.blue600 : theme.colors.gray500)};
  border: 1px solid ${(p) => (p.$active ? theme.colors.blue200 : theme.colors.gray300)};
  border-radius: ${theme.radius.x6};
  padding: ${theme.spaces.x6};
  background-color: ${(p) => (p.$active ? theme.colors.blue50 : theme.colors.white)};
`;

const StyledCheckCircleIcon = styled(CheckCircleIcon)`
  color: ${theme.colors.blue500};
  position: absolute;
  bottom: -2px;
  right: -8px;
  color: ${theme.colors.brand500};
  background-color: ${theme.colors.white};
`;

const UsersAIconWrapper = styled(Stack)`
  background-color: ${theme.colors.grayBlue100};
  color: ${theme.colors.grayBlue500};
  border-radius: ${theme.radius.full};
  padding: ${theme.spaces.x6};
`;
